import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { ImageUpload } from "@/components/ui/ImageUpload";
import { SegmentedControl } from "@/components/ui/visual-controls/SegmentedControl";
import { PositionGridSelector } from "@/components/ui/visual-controls/PositionGridSelector";
import {
  Image as ImageIcon,
  Video,
  Plus,
  Trash2,
  ExternalLink,
} from "lucide-react";
import type { SectionElementOverrides } from "@profolio/templates/types";

interface SectionMediaManagerProps {
  overrides: SectionElementOverrides;
  isPro: boolean;
  onFieldChange: <K extends keyof SectionElementOverrides>(
    key: K,
    value: SectionElementOverrides[K]
  ) => void;
  onUpgradeClick: (featureKey?: string) => void;
}

export function SectionMediaManager({
  overrides,
  isPro,
  onFieldChange,
  onUpgradeClick,
}: SectionMediaManagerProps) {
  const [activeMediaItemIndex, setActiveMediaItemIndex] = useState<number>(0);
  const deleteMedia = trpc.media.deleteMedia.useMutation();

  const mediaItems = overrides.mediaItems || [];

  const handleAddMediaItem = () => {
    if (!isPro) {
      onUpgradeClick("layout_overrides");
      return;
    }
    const next = [...mediaItems, { url: "", alt: "", caption: "", linkUrl: "" }];
    onFieldChange("mediaItems", next);
    setActiveMediaItemIndex(next.length - 1);
  };

  const handleUpdateMediaItem = (
    idx: number,
    field: "url" | "alt" | "caption" | "linkUrl",
    val: string
  ) => {
    if (!isPro) {
      onUpgradeClick("layout_overrides");
      return;
    }
    const next = mediaItems.map((item, i) => (i === idx ? { ...item, [field]: val } : item));
    onFieldChange("mediaItems", next);
  };

  const handleRemoveMediaItem = async (idx: number) => {
    if (!isPro) {
      onUpgradeClick("layout_overrides");
      return;
    }
    const item = mediaItems[idx];
    if (item && item.url) {
      const confirmed = window.confirm(
        "Are you sure you want to remove this media item? This action will permanently delete it from your cloud storage and site."
      );
      if (!confirmed) return;
      try {
        await deleteMedia.mutateAsync({ url: item.url });
      } catch (err) {
        console.warn("Failed to delete media item from storage:", err);
      }
    }
    const next = mediaItems.filter((_, i) => i !== idx);
    onFieldChange("mediaItems", next);
    setActiveMediaItemIndex(Math.max(0, idx - 1));
  };

  return (
    <div className="space-y-5">
      {/* ── 1. Section Background Graphic / Banner ── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-[11px] font-semibold text-foreground flex items-center gap-1.5">
            <ImageIcon className="h-3.5 w-3.5 text-primary" />
            <span>Section Background Image</span>
          </Label>
          <span className="text-[10px] text-muted-foreground">Watermark or textured backdrop</span>
        </div>

        <ImageUpload
          value={overrides.sectionBackgroundImageUrl || null}
          onChange={(url) => onFieldChange("sectionBackgroundImageUrl", url || null)}
          onRemove={async () => {
            if (overrides.sectionBackgroundImageUrl) {
              const confirmed = window.confirm(
                "Are you sure you want to delete this background graphic? This will remove it from cloud storage and your site."
              );
              if (!confirmed) return;
              try {
                await deleteMedia.mutateAsync({ url: overrides.sectionBackgroundImageUrl });
              } catch (err) {
                console.warn("Failed to delete background graphic from storage:", err);
              }
            }
            onFieldChange("sectionBackgroundImageUrl", null);
          }}
          category="figure"
          className="w-full"
        />

        {overrides.sectionBackgroundImageUrl && (
          <div className="space-y-4 pt-2 text-xs">
            {/* Attachment Scroll Behavior */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-foreground">
                Attachment (Scroll Behavior)
              </Label>
              <SegmentedControl
                value={
                  overrides.sectionBackgroundAttachment ||
                  (overrides.sectionBackgroundPosition === "fixed" || overrides.sectionScrollEffect === "parallax"
                    ? "fixed"
                    : "scroll")
                }
                disabled={!isPro}
                onChange={(val) => {
                  onFieldChange(
                    "sectionBackgroundAttachment",
                    val as NonNullable<SectionElementOverrides["sectionBackgroundAttachment"]>
                  );
                }}
                options={[
                  { value: "scroll", label: "Scroll with Page (Standard)" },
                  { value: "fixed", label: "Fixed (Stay Fixed / Parallax Window)" },
                ]}
              />
            </div>

            {/* Focal Position & Fit */}
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-foreground">
                  Focal Position Anchor
                </Label>
                <div className="pt-1">
                  <PositionGridSelector
                    value={overrides.sectionBackgroundPosition || "center"}
                    disabled={!isPro}
                    onChange={(pos) => onFieldChange("sectionBackgroundPosition", pos as any)}
                  />
                </div>
              </div>

              <div className="space-y-1.5 w-full">
                <Label className="text-xs font-semibold text-foreground">Fit & Sizing</Label>
                <Select
                  value={
                    overrides.sectionBackgroundFit ||
                    (overrides.sectionBackgroundPosition === "contain" ? "contain" : "cover")
                  }
                  disabled={!isPro}
                  onChange={(e) =>
                    onFieldChange(
                      "sectionBackgroundFit",
                      e.target.value as NonNullable<SectionElementOverrides["sectionBackgroundFit"]>
                    )
                  }
                  className="h-9 text-xs bg-card w-full"
                >
                  <option value="cover">Cover Fill (Full Section)</option>
                  <option value="contain">Contain Fit (Uncropped)</option>
                  <option value="repeat">Tiled / Watermark Pattern</option>
                  <option value="auto">Natural Size (Original)</option>
                </Select>
              </div>

              <div className="space-y-1.5 w-full">
                <Label className="text-xs font-semibold text-foreground">Overlay Darkening</Label>
                <Select
                  value={overrides.sectionBackgroundOverlay || "none"}
                  disabled={!isPro}
                  onChange={(e) =>
                    onFieldChange(
                      "sectionBackgroundOverlay",
                      e.target.value as NonNullable<SectionElementOverrides["sectionBackgroundOverlay"]>
                    )
                  }
                  className="h-9 text-xs bg-card w-full"
                >
                  <option value="none">None (0%)</option>
                  <option value="rgba(0,0,0,0.1)">Faint (10%)</option>
                  <option value="rgba(0,0,0,0.25)">Subtle (25%)</option>
                  <option value="rgba(0,0,0,0.5)">Balanced (50%)</option>
                  <option value="rgba(0,0,0,0.75)">Heavy (75%)</option>
                </Select>
              </div>

              <div className="space-y-1.5 w-full">
                <Label className="text-xs font-semibold text-foreground">Scroll Motion & Animation</Label>
                <Select
                  value={overrides.sectionScrollEffect || "none"}
                  disabled={!isPro}
                  onChange={(e) =>
                    onFieldChange(
                      "sectionScrollEffect",
                      e.target.value as NonNullable<SectionElementOverrides["sectionScrollEffect"]>
                    )
                  }
                  className="h-9 text-xs bg-card w-full"
                >
                  <option value="none">None (Standard Page Flow)</option>
                  <option value="slide-up">Slide Up on Scroll</option>
                  <option value="fade-in">Fade In on Scroll</option>
                  <option value="parallax">Parallax Motion Effect</option>
                </Select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── 2. Companion Media & Rich Embeds (Split Layouts) ── */}
      <div className="space-y-3 pt-3 border-t border-border/50">
        <div className="flex items-center justify-between">
          <Label className="text-[11px] font-semibold text-foreground flex items-center gap-1.5">
            <Video className="h-3.5 w-3.5 text-primary" />
            <span>Companion Media (Split Layout)</span>
          </Label>
          <span className="text-[10px] text-muted-foreground">Figure, Gallery, Video</span>
        </div>

        <div className="space-y-3 text-xs w-full">
          {/* Media Type */}
          <div className="space-y-1.5 w-full">
            <Label className="text-xs font-semibold text-foreground">Media Type</Label>
            <Select
              value={overrides.mediaType || "none"}
              disabled={!isPro}
              onChange={(e) =>
                onFieldChange(
                  "mediaType",
                  e.target.value as NonNullable<SectionElementOverrides["mediaType"]>
                )
              }
              className="h-9 text-xs bg-card w-full"
            >
              <option value="none">None (Full Content Width)</option>
              <option value="image">Single Figure Image</option>
              <option value="image-gallery">Multi-Image Gallery</option>
              <option value="carousel">Interactive Touch Carousel</option>
              <option value="video">Video Embed (YouTube / Vimeo / MP4)</option>
            </Select>
          </div>

          {/* Media Position & Split Ratio */}
          {overrides.mediaType && overrides.mediaType !== "none" && (
            <>
              <div className="space-y-1.5 w-full">
                <Label className="text-xs font-semibold text-foreground">Media Position</Label>
                <SegmentedControl
                  value={overrides.mediaPosition || "split-right"}
                  disabled={!isPro}
                  onChange={(val) =>
                    onFieldChange(
                      "mediaPosition",
                      val as NonNullable<SectionElementOverrides["mediaPosition"]>
                    )
                  }
                  options={[
                    { value: "split-left", label: "Split Left" },
                    { value: "split-right", label: "Split Right" },
                    { value: "top", label: "Top Banner" },
                    { value: "bottom", label: "Bottom Banner" },
                  ]}
                />
              </div>

              {/* Split Ratio */}
              {overrides.mediaPosition !== "top" && overrides.mediaPosition !== "bottom" && (
                <div className="space-y-1.5 w-full">
                  <Label className="text-xs font-semibold text-foreground">Split Ratio (Content / Media)</Label>
                  <SegmentedControl
                    value={overrides.mediaSplitRatio || "60-40"}
                    disabled={!isPro}
                    onChange={(val) =>
                      onFieldChange(
                        "mediaSplitRatio",
                        val as NonNullable<SectionElementOverrides["mediaSplitRatio"]>
                      )
                    }
                    options={[
                      { value: "70-30", label: "70 / 30" },
                      { value: "60-40", label: "60 / 40" },
                      { value: "50-50", label: "50 / 50" },
                      { value: "40-60", label: "40 / 60" },
                      { value: "30-70", label: "30 / 70" },
                    ]}
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* Media Details Configuration */}
        {overrides.mediaType && overrides.mediaType !== "none" && (
          <div className="space-y-3 bg-muted/30 p-3 rounded-lg border text-xs">
            {/* Video URL Input */}
            {overrides.mediaType === "video" && (
              <div className="space-y-1.5">
                <Label className="text-[11px] font-semibold">Video URL (YouTube, Vimeo, or MP4)</Label>
                <input
                  type="text"
                  value={overrides.mediaVideoUrl || ""}
                  placeholder="e.g. https://www.youtube.com/watch?v=... or .mp4"
                  disabled={!isPro}
                  onChange={(e) => onFieldChange("mediaVideoUrl", e.target.value || null)}
                  className="h-8 text-xs font-mono px-2.5 rounded-lg border border-border bg-card w-full"
                />
              </div>
            )}

            {/* Gallery Layout */}
            {overrides.mediaType === "image-gallery" && (
              <div className="space-y-1.5 w-full">
                <Label className="text-xs font-semibold text-foreground">Gallery Layout Arrangement</Label>
                <SegmentedControl
                  value={overrides.mediaGalleryLayout || "stacked"}
                  disabled={!isPro}
                  onChange={(val) =>
                    onFieldChange(
                      "mediaGalleryLayout",
                      val as NonNullable<SectionElementOverrides["mediaGalleryLayout"]>
                    )
                  }
                  options={[
                    { value: "stacked", label: "Stacked Vertical" },
                    { value: "grid", label: "2-Col Grid" },
                    { value: "row", label: "Horizontal Row" },
                  ]}
                />
              </div>
            )}

            {/* Media Framing & Geometry */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-border/40">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-foreground">Aspect Ratio</Label>
                <SegmentedControl
                  value={overrides.mediaAspectRatio || "auto"}
                  disabled={!isPro}
                  onChange={(val) =>
                    onFieldChange(
                      "mediaAspectRatio",
                      val as NonNullable<SectionElementOverrides["mediaAspectRatio"]>
                    )
                  }
                  options={[
                    { value: "auto", label: "Auto" },
                    { value: "16-9", label: "16:9" },
                    { value: "4-3", label: "4:3" },
                    { value: "1-1", label: "1:1" },
                    { value: "3-4", label: "3:4" },
                  ]}
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-foreground">Corner Radius</Label>
                <SegmentedControl
                  value={overrides.mediaBorderRadius || "md"}
                  disabled={!isPro}
                  onChange={(val) =>
                    onFieldChange(
                      "mediaBorderRadius",
                      val as NonNullable<SectionElementOverrides["mediaBorderRadius"]>
                    )
                  }
                  options={[
                    { value: "none", label: "0px" },
                    { value: "sm", label: "4px" },
                    { value: "md", label: "8px" },
                    { value: "lg", label: "16px" },
                    { value: "full", label: "Pill" },
                  ]}
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-foreground">Shadow & Elevation</Label>
                <SegmentedControl
                  value={overrides.mediaShadow || "subtle"}
                  disabled={!isPro}
                  onChange={(val) =>
                    onFieldChange(
                      "mediaShadow",
                      val as NonNullable<SectionElementOverrides["mediaShadow"]>
                    )
                  }
                  options={[
                    { value: "none", label: "None" },
                    { value: "subtle", label: "Subtle" },
                    { value: "elevated", label: "Elevated" },
                    { value: "3d", label: "3D" },
                    { value: "glow", label: "Glow" },
                  ]}
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-foreground">Frame Bevel</Label>
                <SegmentedControl
                  value={overrides.mediaBevel || "none"}
                  disabled={!isPro}
                  onChange={(val) =>
                    onFieldChange(
                      "mediaBevel",
                      val as NonNullable<SectionElementOverrides["mediaBevel"]>
                    )
                  }
                  options={[
                    { value: "none", label: "None" },
                    { value: "light", label: "Light" },
                    { value: "medium", label: "Medium" },
                    { value: "high", label: "High" },
                  ]}
                />
              </div>
            </div>

            {/* Image / Gallery / Carousel Manager */}
            {(overrides.mediaType === "image" ||
              overrides.mediaType === "image-gallery" ||
              overrides.mediaType === "carousel") && (
              <div className="space-y-2 pt-1 border-t border-border/40">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-foreground">
                    {overrides.mediaType === "carousel"
                      ? "Carousel Slides (Touch Carousel)"
                      : overrides.mediaType === "image"
                      ? "Figure Image"
                      : "Gallery Items"}
                  </span>
                  {overrides.mediaType !== "image" && (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      disabled={!isPro}
                      onClick={handleAddMediaItem}
                      className="h-6 px-2.5 text-[10px] font-medium flex items-center gap-1 cursor-pointer bg-muted hover:bg-muted/80 text-foreground border-0 shadow-2xs rounded-md"
                    >
                      <Plus className="h-3 w-3" />
                      <span>{overrides.mediaType === "carousel" ? "Add Slide" : "Add Image"}</span>
                    </Button>
                  )}
                </div>

                {mediaItems.length === 0 ? (
                  <div className="p-3 text-center border border-dashed rounded-lg bg-muted/20">
                    <p className="text-[11px] text-muted-foreground mb-2">
                      {overrides.mediaType === "carousel"
                        ? "No carousel slides added yet. An interactive placeholder slide is shown in the live preview."
                        : "No media images added yet."}
                    </p>
                    <Button
                      type="button"
                      size="sm"
                      disabled={!isPro}
                      onClick={handleAddMediaItem}
                      className="h-7 text-xs cursor-pointer bg-primary text-primary-foreground hover:bg-primary/90 border-0 shadow-2xs font-medium"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      {overrides.mediaType === "carousel" ? "Add First Slide" : "Add First Image"}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Item tabs if > 1 */}
                    {mediaItems.length > 1 && (
                      <div className="flex flex-wrap gap-1">
                        {mediaItems.map((_, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => setActiveMediaItemIndex(i)}
                            className={`px-2 py-0.5 rounded text-[10px] font-medium border cursor-pointer ${
                              activeMediaItemIndex === i
                                ? "bg-primary text-primary-foreground border-primary"
                                : "bg-card text-muted-foreground border-border"
                            }`}
                          >
                            Image {i + 1}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Active item editor */}
                    {mediaItems[activeMediaItemIndex] && (
                      <div className="p-3 rounded-lg border border-border bg-card/60 space-y-2.5">
                        <div className="flex items-center justify-between">
                          <span className="text-[11px] font-semibold">
                            Image #{activeMediaItemIndex + 1}
                          </span>
                          {mediaItems.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              disabled={!isPro}
                              onClick={() => handleRemoveMediaItem(activeMediaItemIndex)}
                              className="h-6 px-1.5 text-[10px] text-destructive hover:bg-destructive/10 cursor-pointer"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>

                        <ImageUpload
                          value={mediaItems[activeMediaItemIndex].url || null}
                          onChange={(url) =>
                            handleUpdateMediaItem(activeMediaItemIndex, "url", url || "")
                          }
                          onRemove={() => handleRemoveMediaItem(activeMediaItemIndex)}
                          category="figure"
                          className="w-full"
                        />

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <div className="space-y-0.5">
                            <Label className="text-[10px] text-muted-foreground">Caption</Label>
                            <input
                              type="text"
                              value={mediaItems[activeMediaItemIndex].caption || ""}
                              placeholder="e.g. Figure 1: Apparatus"
                              disabled={!isPro}
                              onChange={(e) =>
                                handleUpdateMediaItem(activeMediaItemIndex, "caption", e.target.value)
                              }
                              className="h-7 text-[11px] px-2 rounded border border-border bg-background w-full"
                            />
                          </div>

                          <div className="space-y-0.5">
                            <Label className="text-[10px] text-muted-foreground">Alt Text</Label>
                            <input
                              type="text"
                              value={mediaItems[activeMediaItemIndex].alt || ""}
                              placeholder="e.g. Photo of equipment"
                              disabled={!isPro}
                              onChange={(e) =>
                                handleUpdateMediaItem(activeMediaItemIndex, "alt", e.target.value)
                              }
                              className="h-7 text-[11px] px-2 rounded border border-border bg-background w-full"
                            />
                          </div>
                        </div>

                        <div className="space-y-0.5">
                          <Label className="text-[10px] text-muted-foreground">Optional Link URL</Label>
                          <div className="flex items-center gap-1.5">
                            <input
                              type="text"
                              value={mediaItems[activeMediaItemIndex].linkUrl || ""}
                              placeholder="https://..."
                              disabled={!isPro}
                              onChange={(e) =>
                                handleUpdateMediaItem(activeMediaItemIndex, "linkUrl", e.target.value)
                              }
                              className="h-7 text-[11px] px-2 rounded border border-border bg-background w-full font-mono"
                            />
                            {mediaItems[activeMediaItemIndex].linkUrl && (
                              <a
                                href={mediaItems[activeMediaItemIndex].linkUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-muted-foreground hover:text-foreground"
                              >
                                <ExternalLink className="h-3.5 w-3.5" />
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
